// Focus handed from one composer to the next across a re-render — a draft
// thread posted, the panel swapping to the thread and its reply composer
// taking over — passes through a moment with no editable focused, and iOS
// lets the keyboard go at that moment: it came back (or did not) a beat
// later, when the reply composer got focus. A hidden text field holds the
// focus, and with it the keyboard, until the next composer takes it; it lets
// go on its own if nothing does.
export const KEYBOARD_KEEPER_ATTR = 'data-keyboard-keeper'
export const KEYBOARD_KEEPER_RELEASE_MS = 1500

let releaseTimer: ReturnType<typeof setTimeout> | null = null

// Whether a handoff is worth holding for: a phone, with the keyboard up for
// the composer that is about to go.
export function shouldHoldKeyboardAcrossHandoff({
    isMobile,
    composerFocused,
}: {
    isMobile: boolean
    composerFocused: boolean
}): boolean {
    return Boolean(isMobile && composerFocused)
}

function ensureKeeper(doc: Document): HTMLInputElement {
    const existing = doc.querySelector<HTMLInputElement>(`input[${KEYBOARD_KEEPER_ATTR}]`)
    if (existing) return existing
    const keeper = doc.createElement('input')
    keeper.type = 'text'
    keeper.setAttribute(KEYBOARD_KEEPER_ATTR, 'true')
    keeper.setAttribute('aria-hidden', 'true')
    keeper.tabIndex = -1
    keeper.autocomplete = 'off'
    // Off the page's flow and sight, at the foot where the keyboard already
    // is; 16px so iOS does not zoom to it.
    keeper.style.cssText =
        'position:fixed;bottom:0;left:0;width:1px;height:1px;opacity:0;border:0;padding:0;margin:0;font-size:16px;pointer-events:none;'
    doc.body.appendChild(keeper)
    return keeper
}

export function holdKeyboardAcrossHandoff(doc: Document = document): boolean {
    const keeper = ensureKeeper(doc)
    try {
        keeper.focus({ preventScroll: true })
    } catch {
        return false
    }
    if (releaseTimer) clearTimeout(releaseTimer)
    releaseTimer = setTimeout(() => {
        releaseTimer = null
        if (doc.activeElement === keeper) keeper.blur()
    }, KEYBOARD_KEEPER_RELEASE_MS)
    return doc.activeElement === keeper
}

export function isKeyboardKeeper(element: Element | null | undefined): boolean {
    return !!element?.hasAttribute?.(KEYBOARD_KEEPER_ATTR)
}
